import { ArrowForward, Warning } from "@mui/icons-material";
import { Backdrop, Button, TextareaAutosize } from "@mui/material";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import DotElastic from "components/DotElastic";
import { STRIPE_KEY } from "constants/env";
import { Price } from "models/Payment";
import { Subscription } from "models/User";
import { MY_PLAN_INDEX } from "pages/UpgradePlanFlow/UpgradePlanFlow";
import {
  cancelPlan,
  getCancellationCharges,
} from "services/SubscriptionService";
import { formatPrice } from "utils/helper";

import styles from "./CancelPlan.module.scss";

interface CancelPlanProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  subscription: Subscription;
}

const CancelPlan: React.FC<CancelPlanProps> = ({ setStep, subscription }) => {
  const [cancellationAmount, setCancellationAmount] = useState<Price | null>(
    null,
  );
  const [isCancelling, setIsCancelling] = useState(false);
  const [feedback, setFeedback] = useState("");
  const stripe = useStripe();

  const warningText = useMemo(() => {
    if (!cancellationAmount || cancellationAmount.unit_amount_decimal <= 0)
      return null;
    return (
      <div className={styles.estimatedCost}>
        <Warning className={styles.warningIcon} />
        <div>
          <span>
            You will be charged{" "}
            <strong>{formatPrice(cancellationAmount)}</strong> for{" "}
          </span>
          {subscription.frames_used > subscription.frames_limit && (
            <span>
              <b>{subscription.frames_used - subscription.frames_limit}</b>{" "}
              frames exported over the {subscription.frames_limit} quota
            </span>
          )}
          {subscription.frames_used > subscription.frames_limit &&
            subscription.autopilot_frames_used >
              subscription.autopilot_frames_limit && <span> and </span>}
          {subscription.autopilot_frames_used >
            subscription.autopilot_frames_limit && (
            <span>
              <b>
                {subscription.autopilot_frames_used -
                  subscription.autopilot_frames_limit}
              </b>{" "}
              autopilot frames exported over the{" "}
              {subscription.autopilot_frames_limit} quota
            </span>
          )}
          {subscription.frames_used <= subscription.frames_limit &&
            subscription.autopilot_frames_used <=
              subscription.autopilot_frames_limit && (
              <span>your current usage</span>
            )}
          .
        </div>
      </div>
    );
  }, [cancellationAmount, subscription]);

  const getCancellationAmount = useCallback(() => {
    getCancellationCharges(subscription?.id).then((items) => {
      setCancellationAmount({
        currency: subscription.plan.price.currency,
        unit_amount_decimal: items.reduce(
          (total, item) => item.price.unit_amount_decimal + total,
          0,
        ),
      });
    });
  }, [subscription]);

  useEffect(() => {
    getCancellationAmount();
  }, [getCancellationAmount]);

  const onCancel = () => {
    if (!stripe) return;
    setIsCancelling(true);
    cancelPlan(subscription?.id, feedback)
      .then(async (data) => {
        if (data.invoice_id) {
          const result = await stripe.confirmPayment({
            clientSecret: data.payment_intent_secret,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: "if_required",
          });

          const { error } = result;
          if (error) {
            toast.error(`Payment failed: ${error.message}`);
          } else {
            toast.success("Payment successful!");
            setStep(MY_PLAN_INDEX);
          }
        } else {
          setStep(MY_PLAN_INDEX);
        }
      })
      .catch((error) => {
        if (error.detail) {
          toast.error(error.detail);
        } else {
          toast.error("Something went wrong");
          console.error(error);
        }
      })
      .finally(() => setIsCancelling(false));
  };

  if (!stripe) return null;

  return (
    <div className={styles.container}>
      {cancellationAmount ? (
        <div className={styles.content}>
          <div className={styles.heading}>Sorry to see you go :(</div>

          <div className={styles.feedback}>
            {!!warningText && warningText}

            <p>Please let us know why you're cancelling</p>
            <TextareaAutosize
              minRows={3}
              maxRows={3}
              className={styles.textArea}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share your feedback here..."
            />
          </div>

          <div className={styles.hope}>
            You can restart your subscription anytime.
          </div>

          <div className={styles.actions}>
            <Button
              className={styles.stayBtn}
              onClick={() => setStep(MY_PLAN_INDEX)}
            >
              Stay Subscribed
            </Button>

            <Button
              disabled={isCancelling}
              className={styles.cancelBtn}
              onClick={onCancel}
            >
              Yes, Cancel it <ArrowForward />
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <DotElastic />
        </div>
      )}

      <Backdrop open={isCancelling}>
        <img src="/loading.gif" alt="" />
      </Backdrop>
    </div>
  );
};

const CancelPlanWrapper: React.FC<CancelPlanProps> = (props) => {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), []);

  return (
    <Elements stripe={stripePromise}>
      <CancelPlan {...props} />
    </Elements>
  );
};

export default CancelPlanWrapper;
